/**
 * Custom theme scripts.
 *
 * Keep this file isolated from compiled bundles (theme.js / vendor.min.js).
 * Each feature should be wrapped in its own IIFE so failures cannot cascade.
 */

/**
 * Quick-add inventory guard.
 *
 * On product cards (collection grids, featured collections, search results),
 * the single-variant "Add to cart" button must respect the variant's available
 * inventory net of what the customer already has in their cart. Without this,
 * a customer with 2 of a 2-unit product can keep clicking the button and
 * Shopify will reject each call server-side.
 *
 * Data contract: product-card.liquid stamps the <product-form> with
 *   data-variant-id, data-inventory-tracked ("1"/"0"),
 *   data-inventory-policy ("deny"/"continue"), data-inventory-quantity.
 * We only enforce when inventory is tracked AND the policy denies overselling.
 *
 * Cart state: seeded from /cart.js on init, kept in sync via the theme's
 * existing "cart:change" event (event.detail.cart carries the full cart JSON)
 * and the "cart:refresh" event (which signals a re-fetch).
 */
(function () {
  var cartQtyByVariant = new Map();
  var pendingObserver = null;

  function syncFromCart(cart) {
    cartQtyByVariant.clear();
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach(function (item) {
        var key = String(item.variant_id);
        cartQtyByVariant.set(key, (cartQtyByVariant.get(key) || 0) + item.quantity);
      });
    }
    updateAllForms();
  }

  function fetchCart() {
    return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) syncFromCart(cart); })
      .catch(function () { /* network errors fall back to no-op */ });
  }

  function updateAllForms() {
    document.querySelectorAll('product-form[data-variant-id]').forEach(updateForm);
  }

  function updateForm(productForm) {
    if (productForm.dataset.inventoryTracked !== '1') return;
    if (productForm.dataset.inventoryPolicy !== 'deny') return;

    var variantId = productForm.dataset.variantId;
    var inventory = parseInt(productForm.dataset.inventoryQuantity, 10);
    if (!variantId || isNaN(inventory)) return;

    var button = productForm.querySelector('button[type="submit"]');
    if (!button) return;

    var inCart = cartQtyByVariant.get(String(variantId)) || 0;
    var remaining = inventory - inCart;

    if (remaining <= 0) {
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-disabled', 'true');
      button.dataset.atMax = 'true';
    } else if (button.dataset.atMax === 'true') {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-disabled');
      delete button.dataset.atMax;
    }
  }

  // Initial seed.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fetchCart);
  } else {
    fetchCart();
  }

  // Stay in sync with cart mutations driven elsewhere in the theme.
  document.addEventListener('cart:change', function (event) {
    if (event.detail && event.detail.cart) {
      syncFromCart(event.detail.cart);
    } else {
      fetchCart();
    }
  });
  document.addEventListener('cart:refresh', fetchCart);

  // Section-rendered grids (filters, pagination, search) inject new product-cards.
  // Debounce updates so we don't thrash on large batches.
  function scheduleUpdate() {
    if (pendingObserver !== null) return;
    pendingObserver = requestAnimationFrame(function () {
      pendingObserver = null;
      updateAllForms();
    });
  }
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('product-form[data-variant-id]')) {
          scheduleUpdate();
          return;
        }
        if (node.querySelector && node.querySelector('product-form[data-variant-id]')) {
          scheduleUpdate();
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();

/**
 * Variant picker — block selection of out-of-stock variants on the main product page.
 *
 * Out-of-stock option values are marked with data-unavailable="true" in
 * snippets/option-value.liquid and snippets/variant-picker.liquid. When the
 * customer clicks one, this capture-phase listener stops the variant picker's
 * own change handler (registered on document.body in theme.js) and redirects
 * the selection to the next available value in the same option group. The
 * variant picker then re-renders price, image, and availability from the
 * valid combination as if the customer had clicked it directly.
 */
(function () {
  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.dataset.unavailable !== 'true') return;
      if (!input.hasAttribute('data-option-position')) return;

      var picker = input.closest('variant-picker[context="main_product"]');
      if (!picker) return;

      event.stopImmediatePropagation();

      var groupRadios = Array.from(
        picker.querySelectorAll(
          'input[type="radio"][name="' + CSS.escape(input.name) + '"][data-option-position]'
        )
      );
      var available = groupRadios.filter(function (radio) {
        return radio.dataset.unavailable !== 'true' && !radio.disabled;
      });
      if (available.length === 0) return;

      var clickedIndex = groupRadios.indexOf(input);
      var next =
        available.find(function (radio) {
          return groupRadios.indexOf(radio) > clickedIndex;
        }) || available[0];

      next.checked = true;
      next.dispatchEvent(new Event('change', { bubbles: true }));
    },
    true
  );
})();

/**
 * Slideshow focus management for hidden slides.
 *
 * <slideshow-carousel> keeps every slide in the DOM and only flips the
 * `is-selected` class on the active one (see SlideshowCarousel.select() in
 * theme.js). Without this, keyboard users could Tab into the buttons, links,
 * and CTAs on off-screen slides — an a11y bug and a focus trap that drags
 * the viewport unexpectedly when the focused element scrolls into view.
 *
 * This script syncs the `inert` attribute (with an aria-hidden fallback for
 * older browsers) onto every non-selected slide. It listens to the existing
 * `carousel:settle` event that the carousel already dispatches after each
 * transition, so it adds zero new event surface and never blocks existing
 * behavior. Initial state is set on load, and theme-editor section reloads
 * are handled via `shopify:section:load`.
 */
(function () {
  function syncInert(carousel) {
    var slides = carousel.querySelectorAll('.slideshow__slide');
    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      if (slide.classList.contains('is-selected')) {
        slide.removeAttribute('inert');
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('inert', '');
        slide.setAttribute('aria-hidden', 'true');
      }
    }
  }

  function init(carousel) {
    if (carousel.dataset.inertSyncReady === '1') return;
    carousel.dataset.inertSyncReady = '1';
    syncInert(carousel);
    carousel.addEventListener('carousel:settle', function () {
      syncInert(carousel);
    });
  }

  function initAll(root) {
    var scope = root || document;
    var carousels = scope.querySelectorAll
      ? scope.querySelectorAll('slideshow-carousel')
      : [];
    for (var i = 0; i < carousels.length; i++) init(carousels[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();

/**
 * Product gallery → variant sync.
 *
 * Prestige already handles variant → image (see ProductGallery.onVariantChange
 * in theme.js, which scrolls the carousel to variant.featured_media). This
 * adds the reverse: clicking a gallery image or thumbnail with a
 * data-media-id activates the variant whose featured_media.id matches.
 *
 * Mechanics: look up the variant via /products/{handle}.js (cached per
 * handle), then check the matching option{N} radio inputs in the live
 * <variant-picker> and dispatch a single change event. The picker's own
 * body-delegated change handler (theme.js) takes over from there —
 * selectCombination() re-renders the section, updates the URL, and fires
 * variant:change. No DOM is replaced by this script and no theme code is
 * patched.
 *
 * Loop guard: when the variant changes, the gallery scrolls to the new
 * featured_media, but the click that started it came from that same image —
 * by the time any follow-up click fires, currentVariant.featured_media.id
 * already equals the clicked media id, so we no-op.
 *
 * Bail safely when:
 *   - the gallery has no associated form (placeholder rendering)
 *   - no <variant-picker> is on the page (single-variant product)
 *   - no available variant has the clicked media as its featured_media
 *   - the matched variant is already the selected one
 */
(function () {
  var variantsByHandle = new Map();

  function loadVariants(handle) {
    if (!variantsByHandle.has(handle)) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var url = root + 'products/' + encodeURIComponent(handle) + '.js';
      var promise = fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { return (p && Array.isArray(p.variants)) ? p.variants : []; })
        .catch(function () { return []; });
      variantsByHandle.set(handle, promise);
    }
    return variantsByHandle.get(handle);
  }

  // When more than one variant shares a featured_media, prefer the one that
  // requires changing the fewest option values from the current selection.
  function pickClosestVariant(matches, currentVariant) {
    if (matches.length === 1 || !currentVariant) return matches[0];
    var best = matches[0];
    var bestScore = -1;
    for (var i = 0; i < matches.length; i++) {
      var v = matches[i];
      var score = 0;
      for (var n = 1; n <= 3; n++) {
        var key = 'option' + n;
        if (v[key] && currentVariant[key] && v[key] === currentVariant[key]) score++;
      }
      if (score > bestScore) { bestScore = score; best = v; }
    }
    return best;
  }

  // The radio inputs use option_value.id (a numeric ID) as their `value`
  // attribute — but /products/{handle}.js returns option1/2/3 as the option
  // *name* (e.g. "No Hood"). We match by reading the input's associated
  // label text instead, which Prestige always renders as the option name.
  function getOptionNameForInput(picker, input) {
    var label = input.closest('label');
    if (!label || !picker.contains(label)) {
      label = input.id ? picker.querySelector('label[for="' + CSS.escape(input.id) + '"]') : null;
    }
    if (!label) return '';
    // Prefer a direct-child <span> — that's where Prestige renders the option
    // name (the dropdown variant also appends a "Sold out" span we want to
    // ignore, and the first span is always the name).
    var span = label.querySelector(':scope > span') || label.querySelector('span');
    var text = span ? span.textContent : label.textContent;
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  function syncVariant(gallery, mediaId) {
    var formId = gallery.getAttribute('form');
    if (!formId) { console.warn('[gallery-sync] BAIL: gallery has no form attribute'); return; }
    var form = document.forms[formId];
    if (!form) { console.warn('[gallery-sync] BAIL: no form with id', formId); return; }
    var picker = document.querySelector('variant-picker[form-id="' + formId + '"]');
    if (!picker) { console.warn('[gallery-sync] BAIL: no <variant-picker> for form-id', formId); return; }
    var handle = picker.getAttribute('handle');
    if (!handle) { console.warn('[gallery-sync] BAIL: <variant-picker> has no handle attribute'); return; }

    console.log('[gallery-sync] looking up variants for handle', handle, 'mediaId=', mediaId);

    loadVariants(handle).then(function (variants) {
      if (!variants.length) { console.warn('[gallery-sync] BAIL: /products/' + handle + '.js returned no variants'); return; }

      var idInput = form.elements && form.elements.namedItem('id');
      var currentId = idInput ? parseInt(idInput.value, 10) : NaN;
      var currentVariant = variants.find(function (v) { return v.id === currentId; });
      console.log('[gallery-sync] current variant', {
        id: currentId,
        found: !!currentVariant,
        featured_media_id: currentVariant && currentVariant.featured_media && currentVariant.featured_media.id
      });

      if (
        currentVariant &&
        currentVariant.featured_media &&
        String(currentVariant.featured_media.id) === String(mediaId)
      ) { console.log('[gallery-sync] no-op: current variant already matches this media'); return; }

      var matches = variants.filter(function (v) {
        return v.available && v.featured_media && String(v.featured_media.id) === String(mediaId);
      });
      if (!matches.length) {
        console.warn('[gallery-sync] BAIL: no AVAILABLE variant has featured_media.id=' + mediaId);
        return;
      }

      var target = pickClosestVariant(matches, currentVariant);
      console.log('[gallery-sync] target variant', {
        id: target.id, option1: target.option1, option2: target.option2, option3: target.option3
      });

      var lastChanged = null;
      for (var i = 1; i <= 3; i++) {
        var optionName = target['option' + i];
        if (!optionName) continue;
        var inputs = picker.querySelectorAll(
          'input[type="radio"][data-option-position="' + i + '"][form="' + formId + '"]'
        );
        var optionNameLower = String(optionName).toLowerCase();
        var matchedInput = null;
        var matchSource = null;
        for (var j = 0; j < inputs.length; j++) {
          // Primary: match by the option-value name stamped on the input
          // (covers both the swatch/block radios and the dropdown radios).
          // Fallback: read the associated <label>'s text — useful if a
          // cached/unedited snippet hasn't picked up the data attribute yet.
          var dataName = (inputs[j].dataset && inputs[j].dataset.optionValueName) || '';
          var inputName = dataName || getOptionNameForInput(picker, inputs[j]);
          if (inputName.toLowerCase() === optionNameLower) {
            matchedInput = inputs[j];
            matchSource = dataName ? 'data-option-value-name' : 'label-text fallback';
            if (!inputs[j].checked) {
              inputs[j].checked = true;
              lastChanged = inputs[j];
            }
            break;
          }
        }
        if (matchedInput) {
          console.log('[gallery-sync] position', i, 'matched via', matchSource, {
            optionName: optionName,
            alreadyChecked: !lastChanged || lastChanged !== matchedInput,
            inputValue: matchedInput.value
          });
        } else {
          console.warn('[gallery-sync] position', i, 'NO MATCH for optionName="' + optionName + '" — inputs scanned:',
            Array.prototype.map.call(inputs, function (inp) {
              return {
                value: inp.value,
                dataName: inp.dataset && inp.dataset.optionValueName,
                labelText: getOptionNameForInput(picker, inp)
              };
            })
          );
        }
      }

      if (lastChanged) {
        console.log('[gallery-sync] dispatching change on input', { name: lastChanged.name, value: lastChanged.value, dataName: lastChanged.dataset.optionValueName });
        lastChanged.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        console.log('[gallery-sync] no input was changed (all already matched target — variant should already be active)');
      }
    });
  }

  document.addEventListener('click', function (event) {
    var clickable = event.target.closest(
      '.product-gallery__media[data-media-id], .product-gallery__thumbnail[data-media-id]'
    );
    if (!clickable) return;
    var gallery = clickable.closest('product-gallery');
    if (!gallery) { console.warn('[gallery-sync] BAIL: clicked .product-gallery__media but no <product-gallery> ancestor'); return; }
    var mediaId = clickable.getAttribute('data-media-id');
    if (!mediaId) return;
    console.log('[gallery-sync] click detected on', clickable.className, 'mediaId=', mediaId);
    syncVariant(gallery, mediaId);
  });

  // One-time confirmation the IIFE registered. Remove after debugging.
  console.log('[gallery-sync] script loaded, click listener attached');

  // Combined products swap the entire section to a different handle via
  // product:rerender with productChange=true. Drop the cache so the next
  // click re-fetches against the new product's variants.
  document.addEventListener('product:rerender', function (event) {
    if (event.detail && event.detail.productChange) {
      variantsByHandle.clear();
    }
  });
})();

/**
 * Companion / bundle product auto-adding.
 *
 * A product can declare companion products that get added to the cart together
 * with the main product on Add to Cart. snippets/buy-buttons.liquid collects
 * every in-stock companion variant id into a comma-separated list stamped on the
 * <buy-buttons> element as data-bundle-variant-ids. Two sources feed that list:
 *   - custom.addon_product — a variant-level product reference metafield.
 *   - my_fields.bundle_product_handle — a product-level single-line text
 *     metafield holding one or more product handles (resolved via all_products).
 * The list may therefore contain multiple ids; we add them all.
 *
 * The attribute lives on <buy-buttons> (not <product-form>) on purpose: on a
 * variant change the theme partially re-renders the section by swapping only
 * the <buy-buttons> element (ProductRerender.onRerender in theme.js), leaving
 * the <product-form> wrapper in place. Stamping the ids on <buy-buttons> means
 * each variant switch brings in that variant's correct companions (or drops the
 * attribute when none are available); an attribute on the persistent
 * <product-form> would freeze at the first render and go stale.
 *
 * Why add companions before the main product: the main add carries a
 * sections_url and bundled-section render, so by adding the companions first the
 * cart drawer / cart count markup the server returns already reflects EVERY line
 * item. There is no second fetch and no visible flash, and it works for every
 * cart type — drawer, message, and the page-cart redirect — because we never
 * depend on the post-add "variant:add" event (which the page cart type skips).
 *
 * Out-of-stock companions are skipped two ways: the liquid omits an id when the
 * variant is unavailable, and each cart/add.js call below is sent with
 * X-Requested-With so Shopify rejects an oversell server-side. Companions are
 * added independently (not as one atomic multi-item request), so one failing
 * item never blocks the others or the main product.
 *
 * Mechanics: a capture-phase submit listener runs before the theme's
 * form-level handler. On the first pass it preventDefault + stopImmediatePropagation
 * to hold back the theme, adds the companions, then re-submits the form. A
 * per-submit flag lets that second submit fall straight through to the theme.
 */
(function () {
  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function addCompanion(variantId, mainVariantId) {
    var item = { id: Number(variantId), quantity: 1 };
    // Link each bundled line item back to the main product's selected variant.
    // The leading underscore makes it a HIDDEN line-item property (not shown on
    // the storefront, cart, or checkout). The cleanup reconciler below uses this
    // property to remove the companion when its main product is removed.
    if (mainVariantId) {
      item.properties = { '_product-relation': mainVariantId };
    }
    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        // Tells Shopify to enforce inventory; an oversell returns non-2xx,
        // which we ignore so the remaining items and the main product still add.
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [item] })
    }).catch(function () {
      /* network error: skip this companion, keep going */
    });
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || typeof form.closest !== 'function') return;
      if (!form.action || form.action.indexOf('/cart/add') === -1) return;

      if (!form.closest('product-form')) return;

      var ids = [];

      // Source A — auto-added companions stamped on the live <buy-buttons>
      // element (custom.addon_product / my_fields.bundle_product_handle). The
      // theme re-renders <buy-buttons> on every variant change, so these ids
      // always reflect the current variant.
      var bundleEl = form.querySelector('[data-bundle-variant-ids]');
      if (bundleEl && bundleEl.dataset.bundleVariantIds) {
        ids = ids.concat(bundleEl.dataset.bundleVariantIds.split(','));
      }

      // Source B — add-ons the customer ticked in the "Product Addons" block.
      // Those checkboxes live outside the form, so scope them by the form id
      // they were stamped with. Disabled (sold-out) boxes can't be checked, but
      // we guard anyway.
      var formId = form.getAttribute('id');
      if (formId) {
        var escaped = window.CSS && CSS.escape ? CSS.escape(formId) : formId;
        var boxes = document.querySelectorAll('input.product-addon__checkbox[data-form-id="' + escaped + '"]');
        Array.prototype.forEach.call(boxes, function (box) {
          if (box.checked && !box.disabled && box.dataset.variantId) {
            ids.push(box.dataset.variantId);
          }
        });
      }

      // Normalize and de-duplicate.
      ids = ids.map(function (s) { return String(s).trim(); }).filter(Boolean);
      ids = ids.filter(function (id, index) { return ids.indexOf(id) === index; });
      if (!ids.length) return;

      // Second pass (our own re-submit): let the theme handle the main add.
      if (form.dataset.bundleHandled === '1') {
        delete form.dataset.bundleHandled;
        return;
      }

      // First pass: hold back the theme handler, add the companions, then re-submit.
      event.preventDefault();
      event.stopImmediatePropagation();

      // The main product's currently-selected variant id. The theme keeps this
      // input in sync with the variant picker, so it is current at submit time.
      var idInput = form.querySelector('[name="id"]');
      var mainVariantId = idInput ? idInput.value : '';

      var submitter = event.submitter;
      Promise.all(ids.map(function (id) {
        return addCompanion(id, mainVariantId);
      })).then(function () {
        form.dataset.bundleHandled = '1';
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter || undefined);
        } else {
          form.submit();
        }
      });
    },
    true
  );
})();

/**
 * Companion / bundle product cleanup.
 *
 * Companion products added by the bundle feature carry a hidden
 * "_product-relation" line-item property whose value is the main product's
 * variant id (see the
 * auto-adding IIFE above). They are independent cart lines, so removing the
 * main product does NOT remove them on its own — the theme's quantity/remove
 * handler only touches the clicked line.
 *
 * This reconciler prunes any companion line whose parent variant is no longer
 * present in the cart. It runs on every cart mutation (cart:change /
 * cart:refresh) and once on load, so it covers removal from the cart drawer,
 * the cart page, and quantity-to-zero — regardless of how it was triggered.
 *
 * It only ever removes a companion when its parent is entirely gone, so it
 * never fires while both are in the cart (e.g. right after adding the bundle,
 * where the main add dispatches cart:change with the main variant present).
 */
(function () {
  var RELATION_KEY = '_product-relation';
  var busy = false;

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function relationOf(item) {
    // cart.js returns line-item properties as an object keyed by property name.
    var props = item && item.properties;
    return props ? props[RELATION_KEY] || '' : '';
  }

  function findOrphans(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    var present = {};
    cart.items.forEach(function (item) { present[String(item.variant_id)] = true; });
    return cart.items.filter(function (item) {
      var rel = relationOf(item);
      return rel && !present[String(rel)];
    });
  }

  function changeLine(key) {
    return fetch(cartRoot() + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  }

  function reconcile(cart) {
    if (busy) return;
    var orphans = findOrphans(cart);
    if (!orphans.length) return;
    busy = true;

    // Remove sequentially: /cart/change.js operates on one line key at a time
    // and concurrent calls can race as line positions shift.
    var chain = Promise.resolve();
    orphans.forEach(function (orphan) {
      chain = chain.then(function () { return changeLine(orphan.key); });
    });

    chain
      .catch(function () { /* swallow; UI refresh below still reflects reality */ })
      .then(function () {
        busy = false;
        var pageType = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType;
        if (pageType === 'cart') {
          window.location.reload();
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      });
  }

  function fetchAndReconcile() {
    fetch(cartRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) reconcile(cart); })
      .catch(function () { /* network error: leave the cart as-is */ });
  }

  document.addEventListener('cart:change', function (event) {
    if (event.detail && event.detail.cart) {
      reconcile(event.detail.cart);
    } else {
      fetchAndReconcile();
    }
  });
  document.addEventListener('cart:refresh', fetchAndReconcile);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fetchAndReconcile);
  } else {
    fetchAndReconcile();
  }
})();

/**
 * Product Addons — single selection.
 *
 * The "Product Addons" block renders checkboxes so a customer can opt an add-on
 * in or out. Business rule: at most one add-on per product may be selected, so
 * when one checkbox is ticked we clear the others in the same form group
 * (grouped by data-form-id). They stay checkboxes rather than radios so the
 * customer can still untick to select none.
 */
(function () {
  document.addEventListener('change', function (event) {
    var box = event.target;
    if (!box || !box.classList || !box.classList.contains('product-addon__checkbox')) return;
    if (!box.checked) return;

    var formId = box.getAttribute('data-form-id');
    if (!formId) return;

    var escaped = window.CSS && CSS.escape ? CSS.escape(formId) : formId;
    var group = document.querySelectorAll('input.product-addon__checkbox[data-form-id="' + escaped + '"]');
    Array.prototype.forEach.call(group, function (other) {
      if (other !== box && other.checked) {
        other.checked = false;
      }
    });
  });
})();
